/**
 * The add-to-cart shooting star: its store and its flight math, the half of it
 * that needs no React and no DOM globals, so node:test can drive it.
 *
 * WHAT PLAYS. The pressed button fades into a white glow, and as the glow fades
 * back a star launches from it, accelerating along a mild arc with a trail that
 * grows as it speeds up, towards the cart control in the nav. On arrival the
 * star is gone, the cart control glows white for a moment, and the cart dot
 * either appears (the first item: it grows from nothing) or, if it was already
 * showing, swells slightly and settles back. The canvas half is
 * src/components/cart-flight-layer.tsx and the arrival half is
 * src/components/cart-indicator.tsx.
 *
 * WHEN IT PLAYS. Only while the layer has switched the store on, which it does
 * from useCosmicEffects().cartFlight (either theme, starfield on or off, no
 * reduced motion, not at checkout), and only for an add that really adds a
 * line: cart-context asks from its Added branch and nowhere else, so an item
 * already in the cart, a tier swap or a full cart never launches one.
 *
 * WHY A MODULE STORE AND NOT CONTEXT. The request has to happen inside the
 * click, synchronously: React clears `currentTarget` once dispatch ends, and a
 * product card's button is unmounted and replaced by the re-render the add
 * causes, so the button's box must be read before either happens. A plain
 * module the cart can call from inside addToCart, read by the header through
 * useSyncExternalStore, does that without threading anything through the
 * provider value (which buy-route.test.ts pins flat).
 *
 * THE DOT CAN NEVER STAY HIDDEN. For a first item the dot is held back until
 * the star lands, so it appears on arrival rather than the instant the cart
 * state commits. The arrival is a timer owned HERE, not the layer's animation
 * loop, so a route change, the layer unmounting, a backgrounded tab (no
 * animation frames) or the gate flipping off can never strand a cart with items
 * and no dot. Switching the store off drops every flight and the hold at once.
 */

import { arcPoint, easeInCubic, trailPoints, type Point } from './shooting-star-draw';

// ---------------------------------------------------------------------------
// Timing and shape
// ---------------------------------------------------------------------------

/** The pressed button's glow rises to full over this long. */
export const GLOW_IN_MS = 180;
/** ...then fades back to nothing over this long, while the star leaves. */
export const GLOW_OUT_MS = 420;
/**
 * The star leaves just before the glow peaks, so it launches out of the
 * brightest moment rather than after the button has visibly started to fade.
 */
export const LAUNCH_AT_MS = 160;
export const FLIGHT_MS = 720;
/** When the store lands a flight: the moment the star reaches the cart. */
export const ARRIVAL_MS = LAUNCH_AT_MS + FLIGHT_MS;

/**
 * The arc's greatest sideways bow, as a fraction of the straight-line
 * distance (see arcPoint). About a seventh reads as deliberate without looking
 * like a lob.
 */
export const FLIGHT_BEND = 0.14;
/**
 * Share of the flight that is plain constant speed; the rest is easeInCubic.
 * A pure cubic sits almost motionless on the button for the first third of the
 * flight, which reads as a stall; a little linear keeps it visibly moving from
 * the first frame while still accelerating hard into the cart.
 */
export const FLIGHT_LINEAR_SHARE = 0.15;
/** The head fades in over this share of the flight rather than popping on. */
export const FLIGHT_FADE_IN = 0.08;
/** With the cart scrolled out of view, the star fades over this last share. */
export const OFFSCREEN_FADE = 0.25;
/** The trail runs back to where the head was this share of the flight ago. */
export const TRAIL_LAG = 0.24;
/**
 * Longest trail, CSS px. The ambient stars' trails are 140 to 230px; this one
 * travels further in less time, and uncapped it would streak half the screen.
 */
export const TRAIL_MAX_PX = 200;
export const TRAIL_SAMPLES = 14;
/** A little heavier than an ambient star (1.3 to 1.8), since it is feedback. */
export const STAR_THICKNESS = 1.9;

/** With the cart off screen the star heads for a point just above the top edge. */
export const TOP_EDGE_Y = -16;
/** ...and never closer than this to either side. */
export const EDGE_INSET = 16;

/** The polite announcement is cleared after this long, so it does not linger as stale text. */
export const ANNOUNCE_CLEAR_MS = 7000;

// ---------------------------------------------------------------------------
// Pure flight math
// ---------------------------------------------------------------------------

/** A box on screen, CSS px in viewport coordinates, plus its corner radius. */
export type FlightBox = { x: number; y: number; w: number; h: number; radius: number };

/** The parts of a DOMRect the math reads. */
export type RectLike = { left: number; top: number; width: number; height: number };

export type Viewport = { width: number; height: number };

/** Where the star is heading. `onScreen` false means the top-edge fallback. */
export type CartTarget = { x: number; y: number; onScreen: boolean };

export type FlightStar = {
  head: Point;
  /** Head first, as drawStarTrail takes it. */
  trail: Point[];
  opacity: number;
  thickness: number;
};

const clamp01 = (v: number) => (v <= 0 ? 0 : v >= 1 ? 1 : v);
const smooth = (v: number) => {
  const c = clamp01(v);
  return c * c * (3 - 2 * c);
};

/** Progress along the arc for a share `p` of the flight time: accelerating. */
export function flightEase(p: number): number {
  const c = clamp01(p);
  return FLIGHT_LINEAR_SHARE * c + (1 - FLIGHT_LINEAR_SHARE) * easeInCubic(c);
}

/**
 * The bend to hand arcPoint so the arc always bows UPWARD on screen, whichever
 * side of the button the cart is on (it sits left of centre on a phone, where
 * the search and cart pack next to the burger, and right of it on a desktop).
 * A fixed sign would bow under the line for half the page.
 */
export function flightBend(p0: Point, p1: Point): number {
  return p1.x - p0.x >= 0 ? -FLIGHT_BEND : FLIGHT_BEND;
}

/** Strength, 0 to 1, of the white glow over the pressed button at `elapsed` ms. */
export function sourceGlow(elapsed: number): number {
  if (elapsed <= 0) return 0;
  if (elapsed < GLOW_IN_MS) return smooth(elapsed / GLOW_IN_MS);
  return 1 - smooth((elapsed - GLOW_IN_MS) / GLOW_OUT_MS);
}

/**
 * The star at `elapsed` ms after the click, flying from `p0` (the button's
 * centre at launch) towards `target`; null before launch and from arrival on.
 *
 * The target is whatever the caller measured THIS frame, so a header that
 * scrolls, or a search box that widens and pushes the cart along, is followed
 * rather than flown past. Arriving on screen, the star simply ends: the cart's
 * own glow takes over at that instant. Heading for the top edge instead, it
 * fades out over the last stretch, since there is nothing there to land on.
 */
export function flightStar(elapsed: number, p0: Point, target: CartTarget): FlightStar | null {
  const p = (elapsed - LAUNCH_AT_MS) / FLIGHT_MS;
  if (!(p >= 0 && p < 1)) return null;
  const p1 = { x: target.x, y: target.y };
  const bend = flightBend(p0, p1);
  const t = flightEase(p);
  const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
  // The trail's tail is where the head was TRAIL_LAG ago, so it lengthens on
  // its own as the star accelerates, up to the pixel cap.
  const lagged = t - flightEase(p - TRAIL_LAG);
  const span = dist > 0 ? Math.min(lagged, TRAIL_MAX_PX / dist) : 0;
  const trail = trailPoints(p0, p1, bend, t, span, TRAIL_SAMPLES);
  let opacity = Math.min(1, p / FLIGHT_FADE_IN);
  if (!target.onScreen) opacity *= Math.min(1, (1 - p) / OFFSCREEN_FADE);
  return { head: arcPoint(p0, p1, bend, t), trail, opacity, thickness: STAR_THICKNESS };
}

/**
 * Pick the cart control to fly to from the boxes of every rendered
 * `[data-cart-target]` (the caller drops elements that render no box).
 *
 * Only one live cart control is ever mounted, but a visible one wins over a
 * hidden one if that ever changes. When none has its centre in the viewport
 * (a product page's header is not sticky and scrolls away) the star heads for
 * just above the top edge at the cart's x, clamped inside the screen. With no
 * cart control at all it heads for the top-right corner, where one usually is.
 */
export function pickCartTarget(rects: readonly RectLike[], viewport: Viewport): CartTarget {
  const boxes = rects.filter((r) => r.width > 0 && r.height > 0);
  const centre = (r: RectLike) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  const inView = (c: Point) => c.x >= 0 && c.x <= viewport.width && c.y >= 0 && c.y <= viewport.height;
  for (const r of boxes) {
    const c = centre(r);
    if (inView(c)) return { x: c.x, y: c.y, onScreen: true };
  }
  const x = boxes.length > 0 ? centre(boxes[0]).x : viewport.width - EDGE_INSET;
  const hi = Math.max(EDGE_INSET, viewport.width - EDGE_INSET);
  return { x: Math.min(hi, Math.max(EDGE_INSET, x)), y: TOP_EDGE_Y, onScreen: false };
}

/**
 * The pressed button's box now. `live` is its current box when the element is
 * still in the document (the product page's buttons are); a product card's
 * button is replaced by the re-render the add causes, so for that one the box
 * captured at the click is moved by however far the page has scrolled since,
 * which keeps the glow on the replacement button sitting in the same place.
 */
export function sourceBoxNow(flight: Pick<CartFlight, 'from' | 'scroll'>, live: RectLike | null, scroll: Point): FlightBox {
  if (live && live.width > 0 && live.height > 0) {
    return { x: live.left, y: live.top, w: live.width, h: live.height, radius: flight.from.radius };
  }
  return {
    ...flight.from,
    x: flight.from.x + (flight.scroll.x - scroll.x),
    y: flight.from.y + (flight.scroll.y - scroll.y),
  };
}

export function boxCentre(b: FlightBox): Point {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}

// ---------------------------------------------------------------------------
// The cart control's dot and arrival glow (src/components/cart-indicator.tsx)
// ---------------------------------------------------------------------------

/** What a cart control renders for the dot and the glow. */
export type CartIndicatorView = {
  /** Render the dot: the cart has items and no first item is still in the air. */
  dot: boolean;
  /**
   * The dot's React key. A fresh one per landing remounts the dot, which is
   * what restarts its animation for a second add in a row.
   */
  dotKey: string;
  /** The landing the dot animates, or null for the plain, still dot. */
  dotAnimation: CartArrival | null;
  /** The arrival glow's React key, or null for no glow. */
  glowKey: string | null;
};

/**
 * The dot and glow for one render. `baseline` is the arrival count this
 * control has already settled (see settleCartIndicatorBaseline); only a
 * landing past it animates anything.
 *
 * The dot and the glow are siblings, so their keys carry different prefixes.
 * They once shared the bare arrival count, and with two children on one key
 * React lost track of the old dot: its node stayed in the document, so the
 * dot outlived an emptied cart and every landing stranded one more.
 */
export function cartIndicatorView(
  hasItems: boolean,
  flight: Pick<CartFlightState, 'holdDot' | 'arrivals' | 'lastArrival'>,
  baseline: number,
): CartIndicatorView {
  const landed = flight.arrivals > baseline;
  return {
    dot: hasItems && !flight.holdDot,
    dotKey: landed ? `dot-${flight.arrivals}` : 'dot',
    dotAnimation: landed ? (flight.lastArrival === 'reveal' ? 'reveal' : 'bump') : null,
    glowKey: landed ? `glow-${flight.arrivals}` : null,
  };
}

/**
 * The baseline to carry into the next render. It starts at the arrival count
 * when the control mounts, so a control that appears later (the storefront's
 * cart link after navigating there, the header's other branch on reaching
 * /cart) does not replay the last landing. It also catches up whenever the
 * dot is absent (cart empty, or held back for a first item): a dot that
 * reappears with no new landing (the cart reloaded at sign-in or refilled from
 * another tab, the gate dropped mid-flight) is a fresh node, and without this
 * it would replay the old landing's reveal or bump. A first item's landing
 * still reveals, because the hold kept the baseline one short of it.
 */
export function settleCartIndicatorBaseline(
  hasItems: boolean,
  holdDot: boolean,
  arrivals: number,
  baseline: number,
): number {
  return !hasItems || holdDot ? arrivals : baseline;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** The pressed button. A DOM Element fits; the store only measures it. */
export type FlightSource = {
  getBoundingClientRect(): RectLike;
  readonly isConnected?: boolean;
};

export type CartFlight = {
  readonly id: number;
  /** The cart line id, so a second request for the same add joins the first. */
  readonly key: string;
  /** The button's box at the click. */
  readonly from: FlightBox;
  /** The page scroll at the click, for sourceBoxNow. */
  readonly scroll: Point;
  readonly source: FlightSource | null;
  /** This add put the first item in the cart, so its arrival reveals the dot. */
  readonly revealsDot: boolean;
  readonly startedAt: number;
};

export type CartArrival = 'reveal' | 'bump';

export type CartFlightState = {
  readonly enabled: boolean;
  readonly flights: readonly CartFlight[];
  /** Landed flights, ever. The cart controls key their arrival animations on it. */
  readonly arrivals: number;
  readonly lastArrival: CartArrival | null;
  /** Hide the cart dot: a first item is on its way and has not landed yet. */
  readonly holdDot: boolean;
  /**
   * What the polite live region says. A flight replaces the "Added to your cart"
   * toast, and the toast was also how a screen reader heard about the add.
   */
  readonly announcement: { readonly id: number; readonly text: string } | null;
};

export type CartFlightRequest = {
  key: string;
  from: FlightSource | null | undefined;
  /** The cart was empty before this add, so the dot appears on arrival. */
  wasEmpty: boolean;
  /** Spoken through the live region when a flight launches (omit for a silent add). */
  announce?: string;
};

export type CartFlightStoreOptions = {
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Page scroll, read at the click. */
  readScroll?: () => Point;
  /** The button's corner radius, so the glow has its shape. */
  readRadius?: (source: FlightSource) => number;
  /**
   * A last check on reduced motion. The gate that switches the store on
   * already excludes it, but this is the one thing that must never animate,
   * so the click asks the system again rather than trust a flag set earlier.
   */
  motionAllowed?: () => boolean;
};

export type CartFlightStore = {
  subscribe(listener: () => void): () => void;
  getSnapshot(): CartFlightState;
  getServerSnapshot(): CartFlightState;
  setEnabled(enabled: boolean): void;
  /**
   * Launch a flight for an add, reading the button's box NOW. True when a
   * flight is showing this add (just launched, or already in the air for the
   * same key), in which case the caller skips its toast; false when nothing
   * will play and the caller confirms the add the ordinary way.
   */
  request(req: CartFlightRequest): boolean;
};

const INITIAL_STATE: CartFlightState = Object.freeze({
  enabled: false,
  flights: Object.freeze([]) as readonly CartFlight[],
  arrivals: 0,
  lastArrival: null,
  holdDot: false,
  announcement: null,
});

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function defaultScroll(): Point {
  return typeof window !== 'undefined' ? { x: window.scrollX, y: window.scrollY } : { x: 0, y: 0 };
}

function defaultRadius(source: FlightSource): number {
  if (typeof getComputedStyle !== 'function') return 0;
  try {
    return parseFloat(getComputedStyle(source as unknown as Element).borderTopLeftRadius) || 0;
  } catch {
    return 0;
  }
}

function defaultMotionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function createCartFlightStore(options: CartFlightStoreOptions = {}): CartFlightStore {
  const now = options.now ?? defaultNow;
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const readScroll = options.readScroll ?? defaultScroll;
  const readRadius = options.readRadius ?? defaultRadius;
  const motionAllowed = options.motionAllowed ?? defaultMotionAllowed;

  let state: CartFlightState = INITIAL_STATE;
  let nextId = 1;
  const listeners = new Set<() => void>();
  const arrivalTimers = new Map<number, unknown>();
  let announceTimer: unknown = null;

  const set = (patch: Partial<CartFlightState>) => {
    state = { ...state, ...patch };
    for (const l of Array.from(listeners)) l();
  };

  const arrive = (id: number) => {
    arrivalTimers.delete(id);
    const landed = state.flights.find((f) => f.id === id);
    if (!landed) return;
    const flights = state.flights.filter((f) => f.id !== id);
    set({
      flights,
      arrivals: state.arrivals + 1,
      lastArrival: landed.revealsDot ? 'reveal' : 'bump',
      holdDot: flights.some((f) => f.revealsDot),
    });
  };

  const announce = (text: string) => {
    if (announceTimer !== null) clearTimer(announceTimer);
    announceTimer = setTimer(() => {
      announceTimer = null;
      set({ announcement: null });
    }, ANNOUNCE_CLEAR_MS);
    return { id: nextId++, text };
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => state,
    getServerSnapshot: () => INITIAL_STATE,
    setEnabled(enabled) {
      if (enabled) {
        if (!state.enabled) set({ enabled: true });
        return;
      }
      if (!state.enabled && state.flights.length === 0 && !state.holdDot) return;
      for (const h of Array.from(arrivalTimers.values())) clearTimer(h);
      arrivalTimers.clear();
      set({ enabled: false, flights: [], holdDot: false });
    },
    request({ key, from, wasEmpty, announce: text }) {
      if (!state.enabled || !from) return false;
      if (state.flights.some((f) => f.key === key)) return true;
      if (!motionAllowed()) return false;
      const r = from.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0) || !Number.isFinite(r.left) || !Number.isFinite(r.top)) return false;
      const flight: CartFlight = {
        id: nextId++,
        key,
        from: { x: r.left, y: r.top, w: r.width, h: r.height, radius: readRadius(from) },
        scroll: readScroll(),
        source: from,
        revealsDot: wasEmpty,
        startedAt: now(),
      };
      arrivalTimers.set(flight.id, setTimer(() => arrive(flight.id), ARRIVAL_MS));
      set({
        flights: [...state.flights, flight],
        holdDot: state.holdDot || wasEmpty,
        ...(text ? { announcement: announce(text) } : {}),
      });
      return true;
    },
  };
}

const store = createCartFlightStore();

export const subscribeCartFlights = store.subscribe;
export const getCartFlightState = store.getSnapshot;
export const getServerCartFlightState = store.getServerSnapshot;
/** Called by CartFlightLayer with useCosmicEffects().cartFlight; off drops every flight and the dot hold. */
export const setCartFlightEnabled = store.setEnabled;
/** Called by cart-context from its Added branch only. */
export const requestCartFlight = store.request;
